import { useNavigate } from 'react-router-dom'
import { AppSuspenseFallback } from '@/components/AppSuspenseFallback'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/hooks/use-translation'
import { useAuthContext } from '../hooks/use-auth-context'
import { useLogout } from '../hooks/use-logout'

/**
 * Maintenance page component that displays when the application is under maintenance.
 * Shows the Referent logo and a message indicating work is in progress.
 */
export function InMaintenancePage() {
  const t = useTranslation()
  const { logout } = useLogout()
  const { user } = useAuthContext()
  const navigate = useNavigate()

  if (!user) {
    navigate('/login', { replace: true })
    return <AppSuspenseFallback />
  }

  return (
    <div className="relative h-full bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-6 md:p-10 overflow-hidden">
      {/* Layout skeleton background */}
      <div className="absolute inset-0">
        {/* Sidebar skeleton */}
        <div className="absolute left-0 top-0 w-64 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
          <div className="p-6 space-y-8">
            {/* Logo area */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24" />
            </div>

            {/* Navigation items */}
            <div className="space-y-2">
              <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
                <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded" />
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-18" />
              </div>
            </div>

            {/* Section divider */}
            <div className="pt-4">
              <div className="h-px bg-gray-200 dark:bg-gray-700 mb-4" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-3" />
            </div>

            {/* Secondary navigation */}
            <div className="space-y-2">
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-18" />
              </div>
            </div>
          </div>

          {/* User section in footer */}
          <div className="mt-auto p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 p-2">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              </div>
            </div>
          </div>
        </div>

        {/* Main content skeleton */}
        <div className="ml-64 p-8 space-y-8 h-full">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48" />
            </div>
            <div className="flex space-x-3">
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-24" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-32" />
            </div>
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-20" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-24" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-28" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-18" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-22" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-14" />
            </div>
          </div>

          {/* Table skeleton */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32" />
                <div className="flex space-x-2">
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-20" />
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-24" />
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* Table header */}
                <div className="grid grid-cols-4 gap-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
                {/* Table rows */}
                {Array.from({ length: 5 }, () => (
                  <div key={Math.random().toString(36)} className="grid grid-cols-4 gap-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full" />
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
                    </div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-12" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay with transparent background but blurred */}
      <div className="absolute inset-0 backdrop-blur-sm bg-white/20 dark:bg-gray-900/20" />

      <div className="relative z-10 flex w-full max-w-5xl flex-col gap-12">
        {/* Main card with glassmorphism effect */}
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-slate-400 via-gray-500 to-zinc-500 rounded-3xl blur opacity-20 group-hover:opacity-30 transition duration-1000 group-hover:duration-200"></div>
          <div className="relative flex flex-col gap-20 bg-white rounded-3xl px-16 py-24 shadow-2xl border border-slate-200/50">
            <div className="absolute top-6 right-6">
              <LanguageSwitcher />
            </div>

            <div className="flex flex-col items-center gap-16">
              {/* Logo with animation */}
              <div className="relative group/logo">
                <a href="/">
                  <img
                    src="/logo.png"
                    alt="Referent"
                    className="relative h-28 w-auto transform group-hover/logo:scale-105 transition-transform duration-500 [filter:brightness(0)_invert(1)]"
                  />
                </a>
              </div>

              {/* Main content with staggered animations */}
              <div className="text-center space-y-12">
                <h1 className="text-5xl md:text-6xl font-black bg-gradient-to-r from-slate-700 via-gray-800 to-zinc-800 bg-clip-text text-transparent leading-tight animate-fade-in-up">
                  {t('auth.maintenance.title')}
                </h1>

                <p className="text-xl text-slate-600 leading-relaxed  mx-auto animate-fade-in-up delay-200">
                  {t('auth.maintenance.greeting', { name: user.name })}{' '}
                  {t('auth.maintenance.subtitle')}
                </p>
              </div>

              {/* Logout button */}
              <div className="animate-fade-in-up delay-400">
                <Button
                  type="button"
                  onClick={logout}
                  className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors duration-200"
                >
                  {t('sidebarNav.logout')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
